import type { FeatureFlagEnvironment } from './feature-flag.types';
import { reconcileFeatureFlagEnvironment } from './feature-flag.hostname';

let currentEnvironment: FeatureFlagEnvironment | null = null;

export interface InitFeatureFlagsOptions {
	environment: FeatureFlagEnvironment;
}

/**
 * Call exactly once at app startup (see each app's main.ts). The passed
 * `environment` comes from a build-time value that can be misconfigured for
 * a given deploy; when running in a browser it is reconciled against the
 * actual hostname so a bad build can never resolve more permissively than
 * the real domain allows (see reconcileFeatureFlagEnvironment).
 */
export function initFeatureFlags({
	environment,
}: InitFeatureFlagsOptions): void {
	const hostname =
		typeof window !== 'undefined' ? window.location?.hostname : undefined;
	currentEnvironment = hostname
		? reconcileFeatureFlagEnvironment(environment, hostname)
		: environment;
}

/**
 * Throws if initFeatureFlags() hasn't run yet. This is intentional: a
 * forgotten init call would otherwise silently resolve every non-`on` flag
 * to false with no diagnostic signal, turning a "flag doesn't work" bug
 * report into a much harder investigation than an immediate startup error.
 */
export function getFeatureFlagEnvironment(): FeatureFlagEnvironment {
	if (currentEnvironment === null) {
		throw new Error(
			'[feature-flags] initFeatureFlags({ environment }) must be called ' +
				'once at app startup before featureFlag() can be used.'
		);
	}
	return currentEnvironment;
}

/** Test-only escape hatch to reset the singleton between test cases. */
export function resetFeatureFlagsForTesting(): void {
	currentEnvironment = null;
}
